import React, { useMemo } from 'react';

import type { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';

import { BORDER_WIDTH, COLOR, FONT, SPACE } from '@/shared/design-tokens/design-tokens';
import { formatMetricValue, formatNumberWithK } from '@/shared/lib/utils/number';

export type DonutChartData = {
  name: string;
  value: number;
  itemStyle?: {
    color?: string;
  };
};

export type DonutChartProps = {
  data: DonutChartData[];
  totalLabelText?: string;
  showLoading?: boolean;
  isTotalVisible?: boolean;
  isLegendVisible?: boolean;
  colorMap?: Record<string, string>;
  containerStyle?: React.CSSProperties;
  tooltipOption?: EChartsOption['tooltip'];
} & EChartsOption;

type CreateChartOptionParams = {
  chartData: DonutChartData[];
  originalData: DonutChartData[];
  isLegendVisible: boolean;
  isTotalVisible: boolean;
  totalLabelText: string;
  totalValue: number;
  visibleSegmentCount: number;
  tooltipOption?: EChartsOption['tooltip'];
};

export const applyColorMap = (data: DonutChartData[], colorMap?: Record<string, string>): DonutChartData[] => {
  if (!colorMap) return data;
  return data.map((item) => ({
    ...item,
    itemStyle: {
      color: colorMap[item.name] || item.itemStyle?.color,
    },
  }));
};

export const createChartOption = ({
  chartData,
  originalData,
  isLegendVisible,
  isTotalVisible,
  totalValue,
  visibleSegmentCount,
  tooltipOption,
}: CreateChartOptionParams) => ({
  tooltip: {
    trigger: 'item',
    formatter: (params: Record<string, unknown>) => {
      if (typeof params.value === 'number' && typeof params.percent === 'number') {
        return `${params.name}: ${formatMetricValue(params.value)} (${formatMetricValue(params.percent)}%)`;
      }

      return '';
    },
    ...tooltipOption,
  },
  series: [
    {
      type: 'pie',
      radius: ['70%', '100%'],
      center: ['50%', '50%'],
      data: chartData,
      label: {
        show: false,
      },
      itemStyle: {
        borderWidth: visibleSegmentCount === 1 ? 0 : BORDER_WIDTH[2],
        borderColor: '#fff',
      },
      emphasis: {
        scale: false,
      },
    },
  ],
  legend: isLegendVisible && {
    show: isLegendVisible,
    bottom: 0,
    left: 'center',
    orient: 'horizontal',
    formatter: (name: string) => {
      const item = originalData.find((d) => d.name === name);
      return item ? `${name} ${formatMetricValue(item.value)}` : name;
    },
    textStyle: {
      fontSize: FONT.SIZE.SM,
    },
    itemGap: SPACE[20],
    itemWidth: SPACE[10],
    itemHeight: SPACE[10],
  },
  graphic: isTotalVisible && {
    show: isTotalVisible,
    type: 'group',
    left: 'center',
    top: 'center',
    children: [
      {
        type: 'text',
        top: SPACE[20],
        style: {
          text: formatNumberWithK(totalValue),
          fontSize: FONT.SIZE['4XL'],
          fontWeight: FONT.WEIGHT.BOLD,
          fill: COLOR.GRAY[10],
          textAlign: 'center',
        },
      },
    ],
  },
});

export const DonutChart = React.memo(
  ({
    data,
    totalLabelText = 'Total',
    showLoading = false,
    isTotalVisible = false,
    isLegendVisible = false,
    colorMap,
    containerClassName,
    tooltipOption,
    ...props
  }: DonutChartProps) => {
    const { totalValue, visibleSegmentCount } = useMemo(() => {
      return data.reduce(
        (acc, item) => {
          acc.totalValue += item.value;
          if (item.value > 0) {
            acc.visibleSegmentCount += 1;
          }
          return acc;
        },
        { totalValue: 0, visibleSegmentCount: 0 }
      );
    }, [data]);

    const chartData = useMemo(() => applyColorMap(data, colorMap), [data, colorMap]);

    const chartOption = useMemo(
      () =>
        createChartOption({
          chartData,
          originalData: data,
          isLegendVisible,
          isTotalVisible,
          totalLabelText,
          totalValue,
          visibleSegmentCount,
          tooltipOption,
        }),
      [chartData, data, isLegendVisible, isTotalVisible, totalLabelText, totalValue, visibleSegmentCount, tooltipOption]
    );

    return (
      <div className={typeof containerClassName === 'string' ? containerClassName : undefined}>
        <ReactECharts
          option={chartOption}
          showLoading={showLoading}
          loadingOption={{ maskColor: 'rgba(248, 248, 248, 0.8)' }}
          {...props}
        />
      </div>
    );
  }
);

DonutChart.displayName = 'DonutChart';
